import { forwardRef } from 'react';
import clsx from 'clsx';
import Inline from '../Inline';
import styles from './PageHeader.module.css';

/** @typedef {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'} HeadingLevel */

/**
 * @typedef {Object} PageHeaderOwnProps
 * @property {import('react').ReactNode} title - The page/section title text.
 * @property {HeadingLevel} [level='h1'] - Which heading tag to render `title` as.
 * @property {string | number} [titleMarginBottom] - Overrides the tight default gap below the
 *   title (before subtitle/tags) — a raw CSS length, or a `var(--space-*)` token.
 * @property {import('react').ReactNode} [icon] - A decorative icon shown before the title.
 * @property {import('react').ReactNode} [subtitle] - Plain text/node shown under the title.
 * @property {import('react').ReactNode[]} [tags] - Tag elements shown under the title, laid out
 *   in a wrapping row (e.g. `vector`'s own `Tag`).
 * @property {import('react').ReactNode} [action] - A button anchored to the top-right of the
 *   title row (e.g. a primary CTA for the page).
 */

/** @typedef {PageHeaderOwnProps & Omit<import('react').HTMLAttributes<HTMLElement>, 'title'>} PageHeaderProps */

/**
 * @param {PageHeaderProps} props
 */
const PageHeader = forwardRef(({
    title,
    level: Level = 'h1',
    titleMarginBottom,
    icon,
    subtitle,
    tags,
    action,
    className,
    ...props
}, ref) => (
    <div ref={ref} className={clsx(styles.pageHeader, className)} {...props}>
        <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
                {icon && <div className={styles.icon}>{icon}</div>}
                <Level className={styles.title} style={titleMarginBottom !== undefined ? { marginBottom: titleMarginBottom } : undefined}>
                    {title}
                </Level>
            </div>
            {action && <div className={styles.action}>{action}</div>}
        </div>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {tags && tags.length > 0 && (
            <Inline space="2xs" align="center" className={styles.tags}>
                {tags}
            </Inline>
        )}
    </div>
));
PageHeader.displayName = 'PageHeader';

export default PageHeader;
